import React, { useState } from 'react';
import { Alert, Button, Drawer } from 'antd';
import { BarChartOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ContadorDelegacion, EmpleadosContadores } from '../../api/empleadosApi';
import { fmtNumero } from '../../fct/react';
import { fct } from '../../fct/tokens/tokens';

/** Texto de la fila que agrupa a los empleados sin delegación (CONTRATO §8). */
const SIN_DELEGACION = 'Sin delegación';

/** El nombre a pintar: el de la delegación, o «Sin delegación» si falta o viene en blanco. */
const nombreDelegacion = (d: ContadorDelegacion): string => d.delegacion?.trim() || SIN_DELEGACION;

interface Props {
  /** Panorama completo (sin filtros); `null` mientras llega o si falló. */
  contadores: EmpleadosContadores | null;
  /** Mensaje de error de la carga de contadores, o `null`. */
  error: string | null;
  onReintentar: () => void;
}

/**
 * Botón «Métricas» de la cabecera del listado: abre un panel lateral con el reparto de empleados por delegación
 * (activos y de baja), que antes ocupaba una franja fija bajo las pestañas. Los datos son los mismos contadores
 * que ya carga la pantalla: abrir el panel no hace ninguna petición nueva. El error de la carga y su reintento
 * viven dentro del panel.
 */
export const MetricasEmpleados: React.FC<Props> = ({ contadores, error, onReintentar }) => {
  const [abierto, setAbierto] = useState(false);
  return (
    <>
      <Button icon={<BarChartOutlined aria-hidden />} aria-haspopup="dialog" onClick={() => setAbierto(true)}>Métricas</Button>
      <Drawer open={abierto} onClose={() => setAbierto(false)} width={420} title="Métricas de empleados" destroyOnClose>
        {error && (
          <Alert
            type="warning"
            showIcon
            message={`No se han podido cargar los contadores. ${error}`}
            action={<Button size="small" icon={<ReloadOutlined />} onClick={onReintentar}>Reintentar</Button>}
            style={{ marginBottom: 12 }}
          />
        )}
        {contadores && contadores.porDelegacion.length > 0 && (
          <ul
            aria-label="Empleados por delegación"
            style={{
              display: 'flex', flexDirection: 'column', gap: 8, listStyle: 'none', margin: 0, padding: 0,
              fontSize: 14, color: fct.tinta['700'],
            }}
          >
            {contadores.porDelegacion.map((d, i) => (
              // Varias filas pueden venir sin id (o con el mismo nombre): la posición completa la clave.
              // Es una lectura sin filtro por delegación; si se añade, no se ofrece cuando `idDelegacion` es nulo.
              <li key={`${d.idDelegacion ?? 'sin'}-${i}`}>
                {nombreDelegacion(d)}{' '}
                <span className="fct-mono">{fmtNumero(d.activos)}</span> activos
                {' · '}
                <span className="fct-mono">{fmtNumero(d.bajas)}</span> de baja
              </li>
            ))}
          </ul>
        )}
      </Drawer>
    </>
  );
};
